import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Save, Trash2, Plus, Copy } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import CountryCodesPicker from "@/components/CountryCodesPicker";
import MarkdownField from "@/components/MarkdownField";
import CalculatorFieldsEditor from "@/components/CalculatorFieldsEditor";
import CalculatorModal from "@/components/CalculatorModal";
import { COUNTRIES } from "@/i18n/countries";

const EMPTY = {
    group_id: "",
    title: "",
    vendor_id: "__none__",
    price: "",
    original_price: "",
    savings_pct: "",
    rating: "",
    reviews: "",
    tag: "__none__",
    image: "",
    description: "",
    highlights: "",
    good_to_know: "",
    status: "active",
    payment_mode: "stripe",
    billing_interval: "__none__",
    vendor_email: "",
    vendor_share_pct: "",
    referral_url: "",
    stripe_config_id: "",
    calculator_enabled: false,
    calculator_source_url: "",
    calculator_fields: [],
    calculator_formula: "",
    hide_price_until_calculated: false,
    is_paid_USD: false,
    country_codes: [],
    additional_group_ids: [],
    disclaimer: "",
};

const IMAGE_OPTIONS = [
    "/panda/panda-checklist.png",
    "/panda/panda-plane.png",
    "/panda/panda-cooking.png",
    "/panda/panda-ipad.png",
    "/panda/panda-virtual.png",
    "/panda/panda-super.png",
    "/panda/panda-yoga.png",
    "/panda/panda-table.png",
    "/panda/panda-wheelchair.png",
];

const TAG_OPTIONS = ["New", "Bestseller", "Exclusive", "Popular"];
const NONE = "__none__";

export default function AdminDealDetail() {
    const { dealId } = useParams();
    const navigate = useNavigate();
    const isNew = dealId === "new";

    const [groups, setGroups] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [stripeConfigs, setStripeConfigs] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [mintingPrice, setMintingPrice] = useState(false);
    const [mintCountry, setMintCountry] = useState("");
    const [calcPreviewOpen, setCalcPreviewOpen] = useState(false);

    useEffect(() => {
        api.get("/admin/products")
            .then((r) => setGroups(r.data.groups))
            .catch((e) => toast.error(formatApiError(e)));
        api.get("/admin/stripe-products")
            .then((r) => setStripeConfigs(r.data || []))
            .catch(() => setStripeConfigs([]));
        api.get("/admin/vendors")
            .then((r) => setVendors(r.data.vendors || []))
            .catch(() => setVendors([]));
    }, []);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        api.get(`/admin/deals/${dealId}`)
            .then((r) => {
                const d = r.data;
                setForm({
                    group_id: d.group_id || "",
                    title: d.title || "",
                    vendor_id: d.vendor_id || NONE,
                    price: d.price ?? "",
                    original_price: d.original_price ?? "",
                    savings_pct: d.savings_pct ?? "",
                    rating: d.rating ?? "",
                    reviews: d.reviews ?? "",
                    tag: d.tag || NONE,
                    image: d.image || "",
                    description: d.description || "",
                    highlights: (d.highlights || []).join("\n"),
                    good_to_know: (d.good_to_know || [])
                        .map((x) => `${x.q} | ${x.a}`)
                        .join("\n"),
                    status: d.status || "active",
                    payment_mode: d.payment_mode || "stripe",
                    billing_interval: d.billing_interval || NONE,
                    vendor_email: d.vendor_email || "",
                    vendor_share_pct: d.vendor_share_pct ?? "",
                    referral_url: d.referral_url || "",
                    stripe_config_id: d.stripe_config_id || "",
                    calculator_enabled: Boolean((d.calculator || {}).enabled),
                    calculator_source_url: (d.calculator || {}).source_url || "",
                    calculator_fields: ((d.calculator || {}).fields || []).map((f) => ({
                        name: f.name || "",
                        label: f.label || "",
                        type: f.type || "text",
                        required: Boolean(f.required),
                        min: f.min ?? "",
                        max: f.max ?? "",
                        options: Array.isArray(f.options)
                            ? f.options
                                  .map((o) => (typeof o === "object" ? o.label ?? o.value : o))
                                  .join(", ")
                            : "",
                    })),
                    calculator_formula: (d.calculator || {}).formula || "",
                    hide_price_until_calculated: Boolean(d.hide_price_until_calculated),
                    is_paid_USD: Boolean(d.is_paid_USD),
                    country_codes: Array.isArray(d.country_codes) ? d.country_codes : [],
                    additional_group_ids: Array.isArray(d.additional_group_ids) ? d.additional_group_ids : [],
                    disclaimer: d.disclaimer || "",
                    // Workflow — read the merged shape (workflow object wins)
                    wf_splash_enabled: Boolean((d.workflow || {}).splash_enabled),
                    wf_splash_title: (d.workflow || {}).splash_title || "",
                    wf_splash_body: (d.workflow || {}).splash_body || "",
                    wf_splash_image_url: (d.workflow || {}).splash_image_url || "",
                    wf_splash_source_url: (d.workflow || {}).splash_source_url || "",
                    wf_booking_enabled: Boolean((d.workflow || {}).booking_enabled),
                    wf_booking_source_url: (d.workflow || {}).booking_source_url || "",
                    wf_select_members_enabled: Boolean((d.workflow || {}).select_members_enabled),
                });
                setMeta({
                    id: d.id,
                    created_at: d.created_at,
                    created_by: d.created_by,
                    updated_at: d.updated_at,
                    updated_by: d.updated_by,
                    stripe_price_id: d.stripe_price_id || null,
                    stripe_product_id: d.stripe_product_id || null,
                    stripe_currency: d.stripe_currency || null,
                    stripe_price_country: d.stripe_price_country || null,
                    stripe_price_minted_at: d.stripe_price_minted_at || null,
                });
            })
            .catch((e) => {
                toast.error(formatApiError(e));
                navigate("/admin/products");
            })
            .finally(() => setLoading(false));
    }, [dealId, isNew, navigate]);

    const set = (k) => (e) => {
        const v = e && e.target ? e.target.value : e;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const autoComputeSavings = () => {
        const p = Number(form.price);
        const o = Number(form.original_price);
        if (o > 0 && p >= 0 && p < o) {
            const pct = Math.round(((o - p) / o) * 100);
            setForm((f) => ({ ...f, savings_pct: String(pct) }));
        }
    };

    const buildPayload = () => {
        const calc = form.calculator_enabled
            ? {
                  enabled: true,
                  source_url: form.calculator_source_url?.trim() || null,
                  fields: (form.calculator_fields || [])
                      .filter((f) => f.name?.trim() && f.label?.trim())
                      .map((f) => ({
                          name: f.name.trim(),
                          label: f.label.trim(),
                          type: f.type || "text",
                          required: Boolean(f.required),
                          ...(f.type === "number" && f.min !== "" ? { min: Number(f.min) } : {}),
                          ...(f.type === "number" && f.max !== "" ? { max: Number(f.max) } : {}),
                          ...(f.type === "select"
                              ? {
                                    options: (f.options || "")
                                        .split(",")
                                        .map((o) => o.trim())
                                        .filter(Boolean),
                                }
                              : {}),
                      })),
                  formula: form.calculator_formula?.trim() || null,
              }
            : null;
        const workflow = {
            splash_enabled: Boolean(form.wf_splash_enabled),
            splash_title: form.wf_splash_title?.trim() || null,
            splash_body: form.wf_splash_body?.trim() || null,
            splash_image_url: form.wf_splash_image_url?.trim() || null,
            splash_source_url: form.wf_splash_source_url?.trim() || null,
            booking_enabled: Boolean(form.wf_booking_enabled),
            booking_source_url: form.wf_booking_source_url?.trim() || null,
            select_members_enabled: Boolean(form.wf_select_members_enabled),
            calculator_enabled: Boolean(form.calculator_enabled),
            calculator_source_url: form.calculator_source_url?.trim() || null,
        };
        return {
            group_id: form.group_id,
            title: form.title.trim(),
            vendor_id: form.vendor_id && form.vendor_id !== NONE ? form.vendor_id : null,
            price: Number(form.price),
            original_price: Number(form.original_price),
            savings_pct: Number(form.savings_pct),
            rating: Number(form.rating),
            reviews: Number(form.reviews),
            tag: form.tag && form.tag !== NONE ? form.tag : null,
            image: form.image || null,
            description: form.description || null,
            highlights:
                form.highlights
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean) || null,
            good_to_know: form.good_to_know
                .split("\n")
                .map((line) => {
                    const idx = line.indexOf("|");
                    if (idx === -1) return null;
                    const q = line.slice(0, idx).trim();
                    const a = line.slice(idx + 1).trim();
                    return q && a ? { q, a } : null;
                })
                .filter(Boolean),
            status: form.status,
            payment_mode: form.payment_mode,
            billing_interval:
                form.billing_interval && form.billing_interval !== NONE
                    ? form.billing_interval
                    : null,
            vendor_email: form.vendor_email || null,
            vendor_share_pct:
                form.vendor_share_pct === "" || form.vendor_share_pct == null
                    ? 0
                    : Number(form.vendor_share_pct),
            referral_url: form.referral_url || null,
            stripe_config_id: form.stripe_config_id || null,
            calculator: calc,
            hide_price_until_calculated: Boolean(form.hide_price_until_calculated),
            is_paid_USD: Boolean(form.is_paid_USD),
            workflow,
            country_codes: form.country_codes || [],
            // Deduplicate + strip the primary group ID from the cross-list
            // (backend treats primary + additional as a UNION so keeping
            // primary in both is redundant and confusing to admins).
            additional_group_ids: (form.additional_group_ids || []).filter(
                (id) => id && id !== form.group_id
            ),
            disclaimer: (form.disclaimer || "").trim() || null,
        };
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = buildPayload();
            if (isNew) {
                const { data } = await api.post("/admin/deals", payload);
                toast.success("Product created");
                navigate(`/admin/products/deals/${data.id}`);
            } else {
                await api.patch(`/admin/deals/${dealId}`, payload);
                toast.success("Product updated");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setConfirmDelete(false);
        try {
            await api.delete(`/admin/deals/${dealId}`);
            toast.success("Product deleted");
            navigate("/admin/products");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    // Server-side clone into a NEW inactive draft. Preserves every field
    // of the source, prefixes the title, then navigates the admin into
    // the copy's edit page so they can tweak country_codes / price /
    // language before activating.
    const duplicate = async () => {
        if (isNew) return;
        setDuplicating(true);
        try {
            const { data } = await api.post(`/admin/deals/${dealId}/duplicate`);
            toast.success(`Copied → "${data.title}" (draft)`);
            navigate(`/admin/products/deals/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDuplicating(false);
        }
    };

    // Mint a Stripe Product+Price in the target market's currency and
    // store the IDs on the deal. Gated: duplicated deals start with no
    // Stripe price so admins must explicitly click here for each new
    // market. Prevents accidental cross-currency checkout failures
    // (e.g. HKD Price ID being used for an SGD checkout).
    const mintStripePrice = async () => {
        if (isNew) return;
        const codes = form.country_codes || [];
        const target = mintCountry || (codes.length === 1 ? codes[0] : "");
        if (!target) {
            toast.error("Pick a country first — this deal is available in multiple markets.");
            return;
        }
        setMintingPrice(true);
        try {
            const { data } = await api.post(
                `/admin/deals/${dealId}/generate-stripe-price`,
                { country: target }
            );
            toast.success(
                `Stripe price minted: ${data.stripe_currency.toUpperCase()} ${data.amount.toFixed(2)} (${data.country})`
            );
            setMeta((m) => ({
                ...m,
                stripe_price_id: data.stripe_price_id,
                stripe_product_id: data.stripe_product_id,
                stripe_currency: data.stripe_currency,
                stripe_price_country: data.country,
                stripe_price_minted_at: new Date().toISOString(),
            }));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setMintingPrice(false);
        }
    };

    if (loading) return <div className="p-10 text-navy-500">Loading…</div>;

    return (
        <>
            <div className="px-6 lg:px-12 pt-6">
                <Link
                    to="/admin/products"
                    data-testid="deal-detail-back"
                    className="inline-flex items-center gap-2 text-sm text-navy-500 hover:text-navy-700"
                >
                    <ArrowLeft size={15} /> Back to products
                </Link>
            </div>

            <PageHeader
                eyebrow={isNew ? "New product" : `Product · ${dealId}`}
                title={isNew ? "Create a new product" : form.title || "Edit product"}
                description={
                    isNew
                        ? "Add a new deal to the Perx catalogue. It goes live immediately."
                        : "Update pricing, savings, imagery and copy. Changes are reflected across the platform instantly."
                }
                testid="admin-deal-detail-header"
                action={
                    !isNew && (
                        <Badge
                            data-testid="deal-status-badge"
                            className={
                                form.status === "active"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 border text-xs px-3 py-1"
                                    : "bg-red-50 text-red-700 border-red-200 border text-xs px-3 py-1"
                            }
                        >
                            {form.status === "active" ? "Live" : "Hidden"}
                        </Badge>
                    )
                }
            />

            <div className="px-6 lg:px-12 py-8">
                <form
                    onSubmit={submit}
                    className="grid lg:grid-cols-[1fr_320px] gap-8 items-start"
                    data-testid="admin-deal-form"
                >
                    <div className="rounded-2xl bg-white shadow-soft p-8 space-y-6">
                        <div className="rounded-xl bg-cream p-5 flex items-start justify-between gap-4">
                            <div>
                                <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-1">
                                    Visibility
                                </div>
                                <div className="text-navy-700 font-medium">
                                    {form.status === "active"
                                        ? "Active — visible on the public site"
                                        : "Inactive — hidden from the public site"}
                                </div>
                                <div className="text-xs text-navy-500 mt-1">
                                    Members and public browsers only see products marked active.
                                </div>
                            </div>
                            <Switch
                                data-testid="deal-status-toggle"
                                checked={form.status === "active"}
                                onCheckedChange={(v) =>
                                    setForm((f) => ({ ...f, status: v ? "active" : "inactive" }))
                                }
                                className="data-[state=checked]:bg-emerald-600 data-[state=unchecked]:bg-navy-100"
                            />
                        </div>
                        <CountryCodesPicker
                            value={form.country_codes}
                            onChange={(codes) => setForm((f) => ({ ...f, country_codes: codes }))}
                            label="Available in"
                            hint="Restrict this product to specific markets. Untick everything to publish globally."
                            testid="deal-country-codes"
                        />

                        {!isNew && (
                            <div
                                data-testid="deal-stripe-price-card"
                                className="rounded-xl border border-navy-100 bg-cream/40 p-5"
                            >
                                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-1">
                                            Stripe price · local currency
                                        </div>
                                        <p className="text-[11px] text-navy-500 max-w-md leading-snug">
                                            Mint a Stripe Price in the target market&apos;s currency. Required before this deal can be checked out cross-border. Duplicated deals start empty — pick the country and click Generate.
                                        </p>
                                    </div>
                                    {meta.stripe_price_id ? (
                                        <Badge
                                            data-testid="deal-stripe-price-status"
                                            className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[11px] px-2.5 py-1"
                                        >
                                            {(meta.stripe_currency || "").toUpperCase()} · {meta.stripe_price_country}
                                        </Badge>
                                    ) : (
                                        <Badge
                                            data-testid="deal-stripe-price-status"
                                            className="bg-amber-50 text-amber-700 border-amber-200 border text-[11px] px-2.5 py-1"
                                        >
                                            Not minted
                                        </Badge>
                                    )}
                                </div>
                                {meta.stripe_price_id && (
                                    <div className="mb-3 text-[11px] font-mono text-navy-600 break-all">
                                        {meta.stripe_price_id}
                                    </div>
                                )}
                                <div className="flex items-end gap-3 flex-wrap">
                                    <div className="flex-1 min-w-[200px]">
                                        <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                            Target market
                                        </Label>
                                        <Select
                                            value={mintCountry || (form.country_codes?.length === 1 ? form.country_codes[0] : "")}
                                            onValueChange={setMintCountry}
                                        >
                                            <SelectTrigger
                                                data-testid="deal-mint-country"
                                                className="mt-2 bg-white border-navy-100 h-11"
                                            >
                                                <SelectValue placeholder="Pick a market" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-white">
                                                {(form.country_codes && form.country_codes.length > 0
                                                    ? COUNTRIES.filter((c) => form.country_codes.includes(c.code))
                                                    : COUNTRIES
                                                ).map((c) => (
                                                    <SelectItem key={c.code} value={c.code}>
                                                        {c.flag} {c.name} · {c.currency}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={mintStripePrice}
                                        disabled={mintingPrice}
                                        data-testid="deal-mint-stripe-price"
                                        className="rounded-full border-gold-500 text-navy-700 hover:bg-gold-50 h-11"
                                    >
                                        {mintingPrice
                                            ? "Generating…"
                                            : meta.stripe_price_id
                                            ? "Regenerate Stripe price"
                                            : "Generate Stripe price"}
                                    </Button>
                                </div>
                                <p className="text-[11px] text-navy-500 mt-3">
                                    ⚠️ Calls the <strong>LIVE Stripe API</strong>. A new Price object appears in your Stripe dashboard, but no customer is charged.
                                </p>
                            </div>
                        )}
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Title" required>
                                <Input
                                    required
                                    value={form.title}
                                    onChange={set("title")}
                                    data-testid="deal-field-title"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Vendor">
                                <Select value={form.vendor_id} onValueChange={set("vendor_id")}>
                                    <SelectTrigger
                                        data-testid="deal-field-vendor"
                                        className="mt-2 bg-white border-navy-100 h-11"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={NONE}>No vendor</SelectItem>
                                        {vendors.map((v) => (
                                            <SelectItem key={v.id} value={v.id}>
                                                {v.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>

                        <Field label="Product group" required>
                            <Select value={form.group_id} onValueChange={set("group_id")}>
                                <SelectTrigger
                                    data-testid="deal-field-group"
                                    className="mt-2 bg-white border-navy-100 h-11"
                                >
                                    <SelectValue placeholder="Pick a group" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {groups.map((g) => (
                                        <SelectItem key={g.id} value={g.id}>
                                            {g.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        {/* Cross-list this deal under additional groups. Primary
                            group above stays canonical; entries here surface
                            the same deal on other catalogue sections
                            (e.g. Travel Insurance → Insurance + Travel). */}
                        <div
                            data-testid="deal-additional-groups"
                            className="rounded-xl border border-navy-100 bg-cream/40 p-4"
                        >
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                        Also list in
                                    </Label>
                                    <p className="text-[11px] text-navy-500 mt-1 max-w-md leading-snug">
                                        Cross-list this deal under other catalogue groups. The primary group above stays canonical; ticking a group here surfaces the same product under that section too.
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                                {groups
                                    .filter((g) => g.id !== form.group_id)
                                    .map((g) => {
                                        const on = (form.additional_group_ids || []).includes(g.id);
                                        return (
                                            <label
                                                key={g.id}
                                                htmlFor={`extra-group-${g.id}`}
                                                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${
                                                    on ? "bg-white shadow-soft" : "hover:bg-white/60"
                                                }`}
                                            >
                                                <Checkbox
                                                    id={`extra-group-${g.id}`}
                                                    data-testid={`deal-additional-group-${g.id}`}
                                                    checked={on}
                                                    onCheckedChange={(next) => {
                                                        const current = form.additional_group_ids || [];
                                                        const updated = next
                                                            ? [...current, g.id]
                                                            : current.filter((x) => x !== g.id);
                                                        setForm((f) => ({ ...f, additional_group_ids: updated }));
                                                    }}
                                                    className="data-[state=checked]:bg-navy-700 data-[state=checked]:border-navy-700"
                                                />
                                                <span className="text-sm text-navy-700">{g.name}</span>
                                            </label>
                                        );
                                    })}
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-4 gap-5">
                            <Field label="Member price ($)" required>
                                <Input
                                    required
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.price}
                                    onChange={set("price")}
                                    onBlur={autoComputeSavings}
                                    data-testid="deal-field-price"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Retail price ($)" required>
                                <Input
                                    required
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.original_price}
                                    onChange={set("original_price")}
                                    onBlur={autoComputeSavings}
                                    data-testid="deal-field-original-price"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Savings %" required>
                                <Input
                                    required
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={form.savings_pct}
                                    onChange={set("savings_pct")}
                                    data-testid="deal-field-savings"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Show price as">
                                <Select value={form.billing_interval} onValueChange={set("billing_interval")}>
                                    <SelectTrigger
                                        data-testid="deal-field-billing-interval"
                                        className="mt-2 bg-white border-navy-100 h-11"
                                    >
                                        <SelectValue placeholder="One-off" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        <SelectItem value={NONE}>One-off</SelectItem>
                                        <SelectItem value="month">Per month</SelectItem>
                                        <SelectItem value="year">Per year</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-[11px] text-navy-500 mt-1 leading-snug">
                                    Display-only — shows "/mo" or "/yr" next to the price. Doesn't change how or when the customer is charged.
                                </p>
                            </Field>
                        </div>

                        <WorkflowToggle
                            testid="deal-hide-price"
                            label="Hide price until calculated"
                            blurb="Listings show only the Save % badge (no $ amounts) and the deal page hides the price until the member runs a calculator quote. Independent of the Product calculator toggle below — useful when a calculator deal should still keep its indicative price hidden."
                            enabled={form.hide_price_until_calculated}
                            onToggle={(v) =>
                                setForm((f) => ({ ...f, hide_price_until_calculated: v }))
                            }
                        />

                        <WorkflowToggle
                            testid="deal-paid-usd"
                            label="Always charge in USD"
                            blurb="Member price and retail price above are read as USD amounts. Checkout and listings always use USD for this deal, regardless of the buyer's country — overrides any per-country Stripe price generated below."
                            enabled={form.is_paid_USD}
                            onToggle={(v) =>
                                setForm((f) => ({ ...f, is_paid_USD: v }))
                            }
                        />

                        <div className="grid sm:grid-cols-3 gap-5">
                            <Field label="Rating (0-5)">
                                <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="5"
                                    value={form.rating}
                                    onChange={set("rating")}
                                    data-testid="deal-field-rating"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Reviews count">
                                <Input
                                    type="number"
                                    min="0"
                                    value={form.reviews}
                                    onChange={set("reviews")}
                                    data-testid="deal-field-reviews"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Tag">
                                <Select value={form.tag} onValueChange={set("tag")}>
                                    <SelectTrigger
                                        data-testid="deal-field-tag"
                                        className="mt-2 bg-white border-navy-100 h-11"
                                    >
                                        <SelectValue placeholder="None" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        <SelectItem value={NONE}>— None —</SelectItem>
                                        {TAG_OPTIONS.map((t) => (
                                            <SelectItem key={t} value={t}>
                                                {t}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>

                        <Field label="Description">
                            <Textarea
                                rows={3}
                                value={form.description}
                                onChange={set("description")}
                                data-testid="deal-field-description"
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500"
                            />
                        </Field>

                        <Field
                            label="Disclaimer"
                            hint="Optional. Renders at the bottom of the deal page — use for insurance / finance compliance blurbs. Supports Markdown: **bold**, *italic*, [link text](https://…), - bullets."
                        >
                            <MarkdownField
                                value={form.disclaimer}
                                onChange={(v) => setForm((f) => ({ ...f, disclaimer: v }))}
                                testid="deal-field-disclaimer"
                                rows={5}
                                placeholder="e.g. Perx Group Ltd is an authorised insurance broker (Licence No. FA1234). Read the [Product Disclosure Statement](https://perx.group/pds) before signing up."
                            />
                        </Field>

                        <div className="rounded-xl border border-navy-100/70 p-5 space-y-5 bg-cream/40">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-1">
                                        Payment & vendor
                                    </div>
                                    <div className="text-sm text-navy-500">
                                        Controls how Perx bills for this product and how the vendor gets paid.
                                    </div>
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-3">
                                {[
                                    {
                                        v: "stripe",
                                        label: "Perx bills via Stripe",
                                        sub: "Members pay Perx directly. You reimburse the vendor.",
                                    },
                                    {
                                        v: "stripe_reimburse",
                                        label: "Perx bills · vendor share",
                                        sub: "Perx collects, vendor gets a % monthly reimbursement.",
                                    },
                                    {
                                        v: "referral",
                                        label: "Referral to vendor",
                                        sub: "Vendor manages payment externally. Perx sends the lead.",
                                    },
                                ].map((opt) => (
                                    <label
                                        key={opt.v}
                                        data-testid={`deal-payment-mode-${opt.v}`}
                                        className={`cursor-pointer rounded-xl border-2 p-4 transition-colors ${
                                            form.payment_mode === opt.v
                                                ? "border-gold-500 bg-white"
                                                : "border-navy-100/70 bg-white hover:border-navy-100"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="payment_mode"
                                            value={opt.v}
                                            checked={form.payment_mode === opt.v}
                                            onChange={() =>
                                                setForm((f) => ({ ...f, payment_mode: opt.v }))
                                            }
                                            className="sr-only"
                                        />
                                        <div className="text-navy-700 font-medium text-sm mb-1">
                                            {opt.label}
                                        </div>
                                        <div className="text-xs text-navy-500 leading-relaxed">
                                            {opt.sub}
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Vendor notification email">
                                    <Input
                                        type="email"
                                        value={form.vendor_email}
                                        onChange={set("vendor_email")}
                                        placeholder="orders@vendor.com"
                                        data-testid="deal-field-vendor-email"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                    />
                                    <p className="text-[11px] text-navy-500 mt-1.5">
                                        Where new purchase alerts and job assignments are sent.
                                    </p>
                                </Field>
                                <Field label="Vendor reimbursement %">
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="1"
                                        value={form.vendor_share_pct}
                                        onChange={set("vendor_share_pct")}
                                        placeholder="e.g. 70"
                                        data-testid="deal-field-vendor-share"
                                        disabled={form.payment_mode === "referral"}
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11 disabled:opacity-60"
                                    />
                                    <p className="text-[11px] text-navy-500 mt-1.5">
                                        Portion of each sale we reimburse to the vendor monthly.
                                    </p>
                                </Field>
                            </div>
                            {form.payment_mode === "referral" && (
                                <Field label="Referral URL" required>
                                    <Input
                                        required
                                        type="url"
                                        value={form.referral_url}
                                        onChange={set("referral_url")}
                                        placeholder="https://vendor.com/apply?ref=perx"
                                        data-testid="deal-field-referral-url"
                                        className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                    />
                                    <p className="text-[11px] text-navy-500 mt-1.5">
                                        Members will be redirected here instead of Stripe checkout.
                                    </p>
                                </Field>
                            )}
                            {form.payment_mode !== "referral" && (
                                <Field label="Stripe product config">
                                    <select
                                        value={form.stripe_config_id || ""}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                stripe_config_id: e.target.value,
                                            }))
                                        }
                                        data-testid="deal-field-stripe-config"
                                        className="mt-2 bg-white border border-navy-100 focus-visible:ring-gold-500 h-11 w-full rounded-md px-3 text-sm"
                                    >
                                        <option value="">
                                            — Auto-generate lookup key —
                                        </option>
                                        {stripeConfigs
                                            .filter((c) => c.active)
                                            .map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.label}
                                                    {c.stripe_coupon_id
                                                        ? " · with coupon"
                                                        : ""}
                                                </option>
                                            ))}
                                    </select>
                                    <p className="text-[11px] text-navy-500 mt-1.5">
                                        Pick a Perx-configured Stripe product to reuse an
                                        existing Stripe Price (and optional coupon). Manage
                                        the list at{" "}
                                        <Link
                                            to="/admin/stripe-products"
                                            className="text-gold-600 hover:underline"
                                        >
                                            Stripe products
                                        </Link>
                                        .
                                    </p>
                                </Field>
                            )}
                        </div>

                        {/* Product workflow — chained checkout experience */}
                        <div
                            className="rounded-2xl border border-navy-100/70 bg-white p-5 space-y-5"
                            data-testid="deal-workflow-block"
                        >
                            <div>
                                <div className="text-xs uppercase tracking-widest text-gold-600">
                                    Workflow
                                </div>
                                <div className="font-display text-lg text-navy-700">
                                    Purchase workflow
                                </div>
                                <p className="text-[11px] text-navy-500 mt-0.5">
                                    Tick any combination. Members will step through them in
                                    order:{" "}
                                    <span className="font-medium text-navy-700">
                                        Splash → Calculator → Select members → Book → Checkout
                                    </span>.
                                </p>
                            </div>

                            {/* Splash */}
                            <WorkflowToggle
                                testid="wf-splash"
                                label="Splash screen"
                                blurb="Show a full-screen intro before checkout."
                                enabled={form.wf_splash_enabled}
                                onToggle={(v) =>
                                    setForm((f) => ({ ...f, wf_splash_enabled: v }))
                                }
                            >
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <Field label="Splash source URL (optional)" hint="If set, we iframe this URL. Leave blank to use the built-in template below.">
                                        <Input
                                            type="url"
                                            value={form.wf_splash_source_url}
                                            onChange={set("wf_splash_source_url")}
                                            data-testid="wf-splash-source"
                                            placeholder="https://vendor.example.com/intro"
                                            className="h-11"
                                        />
                                    </Field>
                                    <Field label="Splash image URL">
                                        <Input
                                            type="url"
                                            value={form.wf_splash_image_url}
                                            onChange={set("wf_splash_image_url")}
                                            data-testid="wf-splash-image"
                                            className="h-11"
                                        />
                                    </Field>
                                    <Field label="Title" className="sm:col-span-2">
                                        <Input
                                            value={form.wf_splash_title}
                                            onChange={set("wf_splash_title")}
                                            data-testid="wf-splash-title"
                                            className="h-11"
                                        />
                                    </Field>
                                    <Field label="Body" className="sm:col-span-2">
                                        <Textarea
                                            rows={3}
                                            value={form.wf_splash_body}
                                            onChange={set("wf_splash_body")}
                                            data-testid="wf-splash-body"
                                        />
                                    </Field>
                                </div>
                            </WorkflowToggle>

                            {/* Calculator */}
                            <WorkflowToggle
                                testid="wf-calc"
                                label="Product calculator"
                                blurb="Insurance-style premium calculator. Built-in Perx calculator lives at /calculator."
                                enabled={form.calculator_enabled}
                                onToggle={(v) =>
                                    setForm((f) => ({ ...f, calculator_enabled: v }))
                                }
                            >
                                <Field
                                    label="Calculator source URL (optional)"
                                    hint="Only used when no fields are defined below. Blank = built-in Perx calculator. URL = opens that page in a new tab when a member requests a quote."
                                >
                                    <Input
                                        type="url"
                                        value={form.calculator_source_url}
                                        onChange={set("calculator_source_url")}
                                        data-testid="wf-calc-source"
                                        placeholder="https://vendor.example.com/quote"
                                        className="h-11"
                                    />
                                </Field>

                                <div className="pt-2 border-t border-navy-100/70">
                                    <div className="flex items-center justify-between mb-3">
                                        <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                            Formula fields (optional)
                                        </Label>
                                        {form.calculator_fields?.length > 0 && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => setCalcPreviewOpen(true)}
                                                data-testid="wf-calc-preview"
                                                className="rounded-full border-gold-500 text-navy-700 hover:bg-gold-50 h-9 text-xs px-4"
                                            >
                                                Preview calculator
                                            </Button>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-navy-500 mb-3">
                                        Defining fields here shows a Perx-branded, in-page calculator
                                        instead of the source URL above or the built-in insurance
                                        calculator.
                                    </p>
                                    <CalculatorFieldsEditor
                                        fields={form.calculator_fields}
                                        onChange={(fields) =>
                                            setForm((f) => ({ ...f, calculator_fields: fields }))
                                        }
                                    />
                                    <Field
                                        label="Formula"
                                        className="mt-4"
                                        hint='JS-style expression using the field names above, e.g. "cover_amount * 0.0015 + (smoker ? 50 : 0)".'
                                    >
                                        <Input
                                            value={form.calculator_formula}
                                            onChange={set("calculator_formula")}
                                            data-testid="wf-calc-formula"
                                            placeholder="cover_amount * 0.0015"
                                            className="h-11 font-mono text-sm"
                                        />
                                    </Field>
                                </div>
                            </WorkflowToggle>

                            {/* Select members */}
                            <WorkflowToggle
                                testid="wf-selmem"
                                label="Select members for purchase"
                                blurb="Members can tick which secondary members this product covers (e.g. insurance). Each ticked member becomes a separate case for the vendor."
                                enabled={form.wf_select_members_enabled}
                                onToggle={(v) =>
                                    setForm((f) => ({ ...f, wf_select_members_enabled: v }))
                                }
                            />

                            {/* Book with vendor (TidyCal) */}
                            <WorkflowToggle
                                testid="wf-book"
                                label="Book with vendor (TidyCal)"
                                blurb="Show the vendor's TidyCal booking page inside the workflow — member picks a slot before checkout."
                                enabled={form.wf_booking_enabled}
                                onToggle={(v) =>
                                    setForm((f) => ({ ...f, wf_booking_enabled: v }))
                                }
                            >
                                <Field
                                    label="TidyCal booking URL"
                                    hint="Paste the vendor's public TidyCal link (e.g. https://tidycal.com/perxvendor/consultation)."
                                >
                                    <Input
                                        type="url"
                                        value={form.wf_booking_source_url}
                                        onChange={set("wf_booking_source_url")}
                                        data-testid="wf-book-source"
                                        placeholder="https://tidycal.com/your-vendor"
                                        className="h-11"
                                    />
                                </Field>
                            </WorkflowToggle>
                        </div>

                        <Field label="Highlights (one per line)">
                            <Textarea
                                rows={3}
                                value={form.highlights}
                                onChange={set("highlights")}
                                placeholder="Instant activation after checkout&#10;Cancel any time inside your member area"
                                data-testid="deal-field-highlights"
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 font-mono text-sm"
                            />
                        </Field>

                        <Field label="Good to know (one FAQ per line — “Question | Answer”)">
                            <Textarea
                                rows={4}
                                value={form.good_to_know}
                                onChange={set("good_to_know")}
                                placeholder={"How do I redeem this deal? | Complete checkout in your Perx dashboard.\nCan I cancel? | Yes, any time from your member area."}
                                data-testid="deal-field-good-to-know"
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 font-mono text-sm"
                            />
                            <p className="text-[11px] text-navy-500 mt-1.5">
                                Separate the question and answer with a <code className="text-navy-700">|</code> character. Lines without both are ignored.
                            </p>
                        </Field>

                        <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
                            {!isNew && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setConfirmDelete(true)}
                                        data-testid="deal-delete"
                                        className="rounded-full border-red-200 text-red-600 hover:bg-red-50 h-11"
                                    >
                                        <Trash2 size={15} className="mr-2" /> Delete product
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={duplicate}
                                        disabled={duplicating}
                                        data-testid="deal-duplicate"
                                        className="rounded-full border-navy-200 text-navy-700 hover:bg-navy-50 h-11"
                                    >
                                        <Copy size={15} className="mr-2" />
                                        {duplicating ? "Copying…" : "Copy for another market"}
                                    </Button>
                                </div>
                            )}
                            <div className="ml-auto flex items-center gap-3">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => navigate("/admin/products")}
                                    className="text-navy-500"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="deal-save"
                                    className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                                >
                                    {isNew ? (
                                        <>
                                            <Plus size={15} className="mr-2" />
                                            {saving ? "Creating…" : "Create product"}
                                        </>
                                    ) : (
                                        <>
                                            <Save size={15} className="mr-2" />
                                            {saving ? "Saving…" : "Save changes"}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <aside className="rounded-2xl bg-white shadow-soft p-6 space-y-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                                Product image
                            </div>
                            {form.image && (
                                <div className="rounded-xl bg-cream p-4 mb-3 flex items-center justify-center">
                                    <img
                                        src={form.image}
                                        alt=""
                                        className="h-32 w-32 object-contain"
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-3 gap-2">
                                {IMAGE_OPTIONS.map((src) => (
                                    <button
                                        key={src}
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, image: src }))}
                                        data-testid={`deal-image-${src.split("/").pop().replace(".png", "")}`}
                                        className={`rounded-lg p-2 border-2 transition-colors ${
                                            form.image === src
                                                ? "border-gold-500 bg-gold-500/10"
                                                : "border-transparent hover:border-navy-100"
                                        }`}
                                    >
                                        <img src={src} alt="" className="h-12 w-12 object-contain mx-auto" />
                                    </button>
                                ))}
                            </div>
                            <Input
                                placeholder="Or paste an image URL"
                                value={form.image}
                                onChange={set("image")}
                                data-testid="deal-field-image-url"
                                className="mt-3 bg-white border-navy-100 focus-visible:ring-gold-500 h-10 text-sm"
                            />
                            <div className="mt-3">
                                <ImageUploader
                                    testid="deal-image-upload"
                                    label="Upload your own image"
                                    onUploaded={(url) =>
                                        setForm((f) => ({ ...f, image: url }))
                                    }
                                />
                                <p className="text-[11px] text-navy-500 mt-1.5 text-center">
                                    PNG / JPG / WebP · max 5 MB
                                </p>
                            </div>
                        </div>

                        {!isNew && (
                            <div className="text-xs text-navy-500 pt-4 border-t border-navy-100/70 space-y-1">
                                <div>ID: <span className="font-mono text-navy-700">{meta.id}</span></div>
                                {meta.created_by && (
                                    <div>Created by {meta.created_by}</div>
                                )}
                                {meta.updated_by && (
                                    <div>Last updated by {meta.updated_by}</div>
                                )}
                            </div>
                        )}
                    </aside>
                </form>
            </div>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this product?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the deal from the Perx catalogue immediately.
                            Members will no longer see it. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="deal-delete-cancel">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            data-testid="deal-delete-confirm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <CalculatorModal
                open={calcPreviewOpen}
                onClose={() => setCalcPreviewOpen(false)}
                deal={{ title: form.title || "Untitled product", vendor: form.vendor_email }}
                config={{
                    title: `${form.title || "Untitled product"} calculator`,
                    fields: (form.calculator_fields || [])
                        .filter((f) => f.name?.trim() && f.label?.trim())
                        .map((f) => ({
                            name: f.name.trim(),
                            label: f.label.trim(),
                            type: f.type || "text",
                            required: Boolean(f.required),
                            min: f.min !== "" ? Number(f.min) : undefined,
                            max: f.max !== "" ? Number(f.max) : undefined,
                            options:
                                f.type === "select"
                                    ? (f.options || "").split(",").map((o) => o.trim()).filter(Boolean)
                                    : undefined,
                        })),
                    formula: form.calculator_formula,
                }}
                onSubmit={() => setCalcPreviewOpen(false)}
            />
        </>
    );
}

function Field({ label, required, hint, className = "", children }) {
    return (
        <div className={className}>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            {children}
            {hint && <div className="text-[11px] text-navy-500 mt-1.5">{hint}</div>}
        </div>
    );
}

function WorkflowToggle({ testid, label, blurb, enabled, onToggle, children }) {
    return (
        <div
            className={`rounded-xl border p-4 transition-colors ${
                enabled
                    ? "border-gold-400 bg-gold-50/40"
                    : "border-navy-100 bg-white"
            }`}
        >
            <label className="flex items-start gap-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => onToggle(e.target.checked)}
                    data-testid={`${testid}-enabled`}
                    className="accent-navy-700 h-4 w-4 mt-1"
                />
                <div className="flex-1">
                    <div className="text-sm font-semibold text-navy-700">{label}</div>
                    <div className="text-[11px] text-navy-500 leading-snug mt-0.5">
                        {blurb}
                    </div>
                </div>
            </label>
            {enabled && children && (
                <div className="mt-3 pl-7 space-y-3">{children}</div>
            )}
        </div>
    );
}
